var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import cookies from 'js-cookie';
import whisper from '@ally/whisper';
import { assert, isFunction, isString } from '@ally/utilitarian';
import { isWGActive, isLocalhost, findsMatchingRoute, getSanitizedRoutes, getWarpDestination, getSanitizedPath, } from './utils';
var defaultLog = whisper({
    silent: process.env.NODE_ENV === 'test',
    nameSpace: '@ally/warpgate',
});
/**
 * Default WG instance options:
 *
 * - log:
 *   A whisper logger.
 * - targetCookie:
 *   Allows overriding the name of the target app cookie.
 *   This shouldn't really be touched.
 * - activeCookie:
 *   Allows overriding the name of the warpgate active cookie.
 *   This shouldn't really be touched.
 * - defaultTarget:
 *   The app to transition to if the given route doesn't belong to the current
 *   app with the given `options.app`.
 * - fallbackTarget:
 *   For string route values, the target app to transition to.
 */
var defaults = {
    log: defaultLog,
    activeCookie: 'wa',
    targetCookie: 'wg',
    defaultTarget: 'ember',
    fallbackTarget: 'react',
    isActive: false,
};
var cleanup = function (_a) {
    var targetCookie = _a.targetCookie;
    return function () {
        return cookies.remove(targetCookie);
    };
};
var configure = function (options) { return (__assign(__assign({}, defaults), options)); };
/**
 * Warpgate is used by non-TS consumers...
 * Be safe. Practice defensive programming.
 */
function inspectConfig(_a) {
    var app = _a.app, getRoutes = _a.getRoutes, activeCookie = _a.activeCookie, targetCookie = _a.targetCookie, defaultTarget = _a.defaultTarget, fallbackTarget = _a.fallbackTarget;
    assert(isString(app), 'Invalid config.app');
    assert(isString(activeCookie), 'Invalid config.activeCookie');
    assert(isString(targetCookie), 'Invalid config.targetCookie');
    assert(isString(defaultTarget), 'Invalid config.defaultTarget');
    assert(isString(fallbackTarget), 'Invalid config.fallbackTarget');
    assert(isFunction(getRoutes), 'Invalid config.getRoutes');
}
/**
 * Given some config, will return a function that warps to the given app
 * `target` and sets the location to `path`.
 */
function warps(_a) {
    var targetCookie = _a.targetCookie;
    return function (target, path, replace) {
        cookies.set(targetCookie, target, {
            path: '/',
            secure: !isLocalhost(),
        });
        if (!path) {
            return window.location.reload();
        }
        var destination = getWarpDestination(path);
        if (replace) {
            return window.location.replace(destination);
        }
        return window.location.assign(destination);
    };
}
/**
 * Given some config will return a function that takes an optional pathname
 * (defaulting to the current location) and decides if warping is necessary.
 *
 * Warping Semantics:
 *
 * DON'T WARP:
 * - A route matching `path` is found and belongs to the current `app`.
 * - No matching route is found and the current app is the `defaultTarget`.
 *
 * WARP WHEN:
 * - A route matching `path` is found, but doesn't belong to the current app.
 * - No matching route is found and this isn't the `defaultTarget`.
 */
function check(config, warp) {
    var findRoute = findsMatchingRoute(config);
    return function (path, doWarp) {
        if (path === void 0) { path = window.location.href; }
        if (doWarp === void 0) { doWarp = true; }
        if (!isWGActive(config))
            return false;
        var log = config.log, defaultTarget = config.defaultTarget;
        var routes = getSanitizedRoutes(config);
        var sanitizedPath = getSanitizedPath(path);
        log.debug({
            message: ["[WG] Resolved Routes:", routes],
        });
        var target = ((routes && findRoute(routes, sanitizedPath)) || {
            target: defaultTarget,
        }).target;
        if (config.app === target) {
            log.info({
                message: ['[WG] Self-owned route.', { sanitizedPath: sanitizedPath, target: target }],
            });
            return false;
        }
        if (doWarp) {
            var msg = "[WG] Transition triggered. Moving to wg=" + target + ".";
            log.info({ message: [msg, { sanitizedPath: sanitizedPath, target: target }] });
            warp(target, sanitizedPath);
        }
        return true;
    };
}
/**
 * Returns a "warpgate factory instance" with the following methods:
 *
 * - warp: A method to forcefully warp.
 * - check: Checks a route and warps if necessary.
 * - cleanup: Removes any cookies set by warpgate.
 */
export default function warpgate(options) {
    var config = configure(options);
    inspectConfig(config);
    var warp = warps(config);
    var isActive = function () { return isWGActive(config); };
    var getRouteSchema = function () { return getSanitizedRoutes(config); };
    return {
        warp: warp,
        check: check(config, warp),
        cleanup: cleanup(config),
        isActive: isActive,
        getRouteSchema: getRouteSchema,
    };
}
export * from './utils';
export * from './types';
//# sourceMappingURL=index.js.map